import { message, Modal, Spin, Upload, UploadFile, UploadProps } from 'antd';
import { Canvas, FabricImage, Rect } from 'fabric';
import { useCallback, useEffect, useRef, useState } from 'react';
import orderApi from '../../apis/order';
import {
  BagPrintArea,
  DesignMaxSize,
  PRINT_AREA_OPTIONS_LABELS,
  TSHIRT_CANVAS_CONFIG,
  TSHIRT_PRINT_AREAS,
  TshirtPrintArea,
} from '../../constants/tshirt';
import {
  IDesign,
  PrintAreaType,
  ProductType,
  Side,
} from '../../interfaces/Order';
import { SpinContainer } from '../Order/Order.styled';
import PrimaryButton from '../PrimaryButton/PrimaryButton';

interface TShirtMockupEditorProps {
  orderId: string;
  onDesignSubmitted: (
    design: IDesign,
    mockupDataUrl?: string,
    selectedPrintArea?: PrintAreaType,
    selectedSide?: Side
  ) => void;
  color?: string;
  side?: Side;
  templateUrl?: string;
  productType?: ProductType;
}

const TShirtMockupEditor = (props: TShirtMockupEditorProps) => {
  const { onDesignSubmitted, color, side, templateUrl, productType } = props;

  // Canvas refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvas = useRef<Canvas | null>(null);
  const fabricImage = useRef<FabricImage | null>(null);
  const tshirtBackgroundImage = useRef<FabricImage | null>(null);

  // State
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [designSubmitted, setDesignSubmitted] = useState(false);

  const [loading, setLoading] = useState(false);
  const [showPrintAreaGuides] = useState(true);
  const [debugMode] = useState(false);
  const [clickCoordinates, setClickCoordinates] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [currentPrintArea, setCurrentPrintArea] = useState<PrintAreaType>(
    productType === ProductType.TSHIRT
      ? PrintAreaType.CenterChest
      : PrintAreaType.BagNormalCenter
  );
  const [currentSide, setCurrentSide] = useState<Side>(side || Side.FRONT);
  const [mockupPreview, setMockupPreview] = useState<string | null>(null);
  const [showMockupModal, setShowMockupModal] = useState(false);
  const [printAreaOptions, setPrintAreaOptions] = useState<PrintAreaType[]>(
    productType === ProductType.TSHIRT ? TshirtPrintArea : BagPrintArea
  );

  // Draw print area guides
  const drawPrintAreaGuides = useCallback(
    (canvas: Canvas) => {
      if (!showPrintAreaGuides) return;

      // Remove existing guides
      const guides = canvas
        .getObjects()
        .filter((obj) => (obj as any).name === 'print-area-guide');
      guides.forEach((guide) => canvas.remove(guide));

      const printArea = TSHIRT_PRINT_AREAS[currentPrintArea];

      const guide = new Rect({
        left: printArea.x,
        top: printArea.y,
        width: printArea.width,
        height: printArea.height,
        fill: 'transparent',
        stroke: 'rgba(0, 123, 255, 0.8)',
        strokeWidth: 2,
        strokeDashArray: [5, 5],
        selectable: false,
        evented: false,
        name: 'print-area-guide',
      });

      canvas.add(guide);
      canvas.renderAll();
    },
    [showPrintAreaGuides, currentPrintArea]
  );

  // Initialize canvas
  useEffect(() => {
    if (!canvasRef.current) return;

    // Initialize Fabric canvas
    fabricCanvas.current = new Canvas(canvasRef.current, {
      preserveObjectStacking: true,
      width: TSHIRT_CANVAS_CONFIG.width,
      height: TSHIRT_CANVAS_CONFIG.height,
    });

    // Load t-shirt image as background
    const imagePath = templateUrl ?? '';
    FabricImage.fromURL(
      imagePath,
      {},
      {
        crossOrigin: 'anonymous',
      }
    )
      .then((img) => {
        // Scale image to fit canvas while maintaining aspect ratio
        const scaleX = TSHIRT_CANVAS_CONFIG.width / img.width!;
        const scaleY = TSHIRT_CANVAS_CONFIG.height / img.height!;
        const scale = Math.min(scaleX, scaleY);

        img.set({
          scaleX: scale,
          scaleY: scale,
          originX: 'center',
          originY: 'center',
          left: TSHIRT_CANVAS_CONFIG.width / 2,
          top: TSHIRT_CANVAS_CONFIG.height / 2,
          selectable: false,
          evented: false,
        });

        tshirtBackgroundImage.current = img;
        fabricCanvas.current!.backgroundImage = img;
        fabricCanvas.current?.renderAll();

        // Draw print area guides
        drawPrintAreaGuides(fabricCanvas.current!);

        // Add debug click handler
        if (debugMode) {
          fabricCanvas.current!.on('mouse:down', (e) => {
            if (e.e && fabricCanvas.current) {
              const pointer = fabricCanvas.current.getPointer(e.e);
              setClickCoordinates({
                x: Math.round(pointer.x),
                y: Math.round(pointer.y),
              });
              console.log(
                `Clicked at: x=${Math.round(pointer.x)}, y=${Math.round(
                  pointer.y
                )}`
              );
            }
          });
        }
      })
      .catch((error) => {
        console.warn('Failed to load t-shirt image:', imagePath, error);
        // Fallback: create a simple colored rectangle as placeholder
        const placeholder = new Rect({
          width: TSHIRT_CANVAS_CONFIG.width * 0.8,
          height: TSHIRT_CANVAS_CONFIG.height * 0.9,
          fill: '#f0f0f0',
          stroke: '#ccc',
          strokeWidth: 2,
          originX: 'center',
          originY: 'center',
          left: TSHIRT_CANVAS_CONFIG.width / 2,
          top: TSHIRT_CANVAS_CONFIG.height / 2,
          selectable: false,
          evented: false,
        });

        fabricCanvas.current?.add(placeholder);
        fabricCanvas.current?.sendObjectToBack(placeholder);
        drawPrintAreaGuides(fabricCanvas.current!);
      });

    return () => {
      fabricCanvas.current?.dispose();
    };
  }, [currentSide, templateUrl, drawPrintAreaGuides, debugMode]);

  // Update t-shirt when template URLs or side changes
  useEffect(() => {
    console.log('templateUrl', templateUrl);
    if (!fabricCanvas.current) return;

    // Load new t-shirt image
    const imagePath = templateUrl ?? '';
    FabricImage.fromURL(
      imagePath,
      {},
      {
        crossOrigin: 'anonymous',
      }
    )
      .then((img) => {
        // Scale image to fit canvas while maintaining aspect ratio
        const scaleX = TSHIRT_CANVAS_CONFIG.width / img.width!;
        const scaleY = TSHIRT_CANVAS_CONFIG.height / img.height!;
        const scale = Math.min(scaleX, scaleY);

        img.set({
          scaleX: scale,
          scaleY: scale,
          originX: 'center',
          originY: 'center',
          left: TSHIRT_CANVAS_CONFIG.width / 2,
          top: TSHIRT_CANVAS_CONFIG.height / 2,
          selectable: false,
          evented: false,
        });

        tshirtBackgroundImage.current = img;
        fabricCanvas.current!.backgroundImage = img;
        fabricCanvas.current?.renderAll();
        drawPrintAreaGuides(fabricCanvas.current!);
      })
      .catch((error) => {
        console.warn('Failed to load t-shirt image:', imagePath, error);
      });
  }, [currentSide, templateUrl, drawPrintAreaGuides]);

  // Update current print area when prop changes
  useEffect(() => {
    setCurrentPrintArea(
      productType === ProductType.TSHIRT
        ? PrintAreaType.CenterChest
        : PrintAreaType.BagNormalCenter
    );
    setPrintAreaOptions(
      productType === ProductType.TSHIRT ? TshirtPrintArea : BagPrintArea
    );
  }, [productType]);

  // Update print area guides when selected area changes
  useEffect(() => {
    if (fabricCanvas.current) {
      drawPrintAreaGuides(fabricCanvas.current);
    }
  }, [currentPrintArea, showPrintAreaGuides, drawPrintAreaGuides]);

  // Handle design upload
  useEffect(() => {
    if (!fabricCanvas.current) return;

    if (fileList.length > 0) {
      FabricImage.fromURL(
        URL.createObjectURL(fileList[0]!.originFileObj as Blob)
      ).then((img) => {
        const printArea = TSHIRT_PRINT_AREAS[currentPrintArea];

        // Position and scale image to fit print area
        img.set({
          left: printArea.x + printArea.width / 2,
          top: printArea.y + printArea.height / 2,
          originX: 'center',
          originY: 'center',
          scaleX:
            Math.min(
              printArea.width / img.width!,
              printArea.height / img.height!
            ) * 0.8,
          scaleY:
            Math.min(
              printArea.width / img.width!,
              printArea.height / img.height!
            ) * 0.8,
          selectable: true,
        });

        // Remove existing design
        if (fabricImage.current) {
          fabricCanvas.current?.remove(fabricImage.current);
        }

        fabricImage.current = img;
        fabricCanvas.current?.add(img);
        fabricCanvas.current?.setActiveObject(img);

        // Add constraint function to keep design within print area
        const constrainToCurrentPrintArea = () => {
          if (!fabricImage.current) return;

          const printArea = TSHIRT_PRINT_AREAS[currentPrintArea];
          const obj = fabricImage.current;

          // Get object bounds
          const objLeft = obj.left!;
          const objTop = obj.top!;
          const objWidth = obj.getScaledWidth();
          const objHeight = obj.getScaledHeight();

          // Calculate object boundaries (considering center origin)
          const objLeftEdge = objLeft - objWidth / 2;
          const objRightEdge = objLeft + objWidth / 2;
          const objTopEdge = objTop - objHeight / 2;
          const objBottomEdge = objTop + objHeight / 2;

          // Calculate print area boundaries
          const printAreaLeft = printArea.x;
          const printAreaRight = printArea.x + printArea.width;
          const printAreaTop = printArea.y;
          const printAreaBottom = printArea.y + printArea.height;

          // Constrain position
          let newLeft = objLeft;
          let newTop = objTop;

          // Horizontal constraints
          if (objLeftEdge < printAreaLeft) {
            newLeft = printAreaLeft + objWidth / 2;
          } else if (objRightEdge > printAreaRight) {
            newLeft = printAreaRight - objWidth / 2;
          }

          // Vertical constraints
          if (objTopEdge < printAreaTop) {
            newTop = printAreaTop + objHeight / 2;
          } else if (objBottomEdge > printAreaBottom) {
            newTop = printAreaBottom - objHeight / 2;
          }

          // Apply constraints if needed
          if (newLeft !== objLeft || newTop !== objTop) {
            obj.set({
              left: newLeft,
              top: newTop,
            });
            fabricCanvas.current?.renderAll();
          }
        };

        // Add event listeners for movement constraints
        img.on('moving', constrainToCurrentPrintArea);
        img.on('scaling', constrainToCurrentPrintArea);
        img.on('rotating', constrainToCurrentPrintArea);

        fabricCanvas.current?.renderAll();
      });
    } else if (fabricImage.current) {
      fabricCanvas.current?.remove(fabricImage.current);
      fabricImage.current = null;
      fabricCanvas.current?.renderAll();
    }
  }, [fileList, currentPrintArea]);

  // Update design constraints when print area changes
  useEffect(() => {
    if (!fabricImage.current || !fabricCanvas.current) return;

    const updateDesignConstraints = () => {
      if (!fabricImage.current) return;

      const printArea = TSHIRT_PRINT_AREAS[currentPrintArea];
      const obj = fabricImage.current;

      // Reposition design to center of new print area if it's outside
      const objLeft = obj.left!;
      const objTop = obj.top!;
      const objWidth = obj.getScaledWidth();
      const objHeight = obj.getScaledHeight();

      // Calculate object boundaries
      const objLeftEdge = objLeft - objWidth / 2;
      const objRightEdge = objLeft + objWidth / 2;
      const objTopEdge = objTop - objHeight / 2;
      const objBottomEdge = objTop + objHeight / 2;

      // Calculate print area boundaries
      const printAreaLeft = printArea.x;
      const printAreaRight = printArea.x + printArea.width;
      const printAreaTop = printArea.y;
      const printAreaBottom = printArea.y + printArea.height;

      // Check if design is completely outside the new print area
      const isOutside =
        objRightEdge < printAreaLeft ||
        objLeftEdge > printAreaRight ||
        objBottomEdge < printAreaTop ||
        objTopEdge > printAreaBottom;

      if (isOutside) {
        // Move to center of new print area
        obj.set({
          left: printArea.x + printArea.width / 2,
          top: printArea.y + printArea.height / 2,
        });
      } else {
        // Apply constraints to keep within bounds
        let newLeft = objLeft;
        let newTop = objTop;

        // Horizontal constraints
        if (objLeftEdge < printAreaLeft) {
          newLeft = printAreaLeft + objWidth / 2;
        } else if (objRightEdge > printAreaRight) {
          newLeft = printAreaRight - objWidth / 2;
        }

        // Vertical constraints
        if (objTopEdge < printAreaTop) {
          newTop = printAreaTop + objHeight / 2;
        } else if (objBottomEdge > printAreaBottom) {
          newTop = printAreaBottom - objHeight / 2;
        }

        if (newLeft !== objLeft || newTop !== objTop) {
          obj.set({
            left: newLeft,
            top: newTop,
          });
        }
      }

      fabricCanvas.current?.renderAll();
    };

    updateDesignConstraints();
  }, [currentPrintArea]);

  // Generate mockup image combining t-shirt and design
  const generateMockup = useCallback((): Promise<string | null> => {
    return new Promise((resolve) => {
      console.log('generateMockup called');
      console.log('fabricCanvas.current:', !!fabricCanvas.current);
      console.log('fabricImage.current:', !!fabricImage.current);

      if (!fabricCanvas.current || !fabricImage.current) {
        console.log('Early return: missing canvas or image');
        resolve(null);
        return;
      }

      // Create a temporary canvas for mockup generation
      const tempCanvas = document.createElement('canvas');
      const ctx = tempCanvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }

      // Set canvas size to match the fabric canvas
      tempCanvas.width = TSHIRT_CANVAS_CONFIG.width;
      tempCanvas.height = TSHIRT_CANVAS_CONFIG.height;

      // Load t-shirt background image
      const tshirtImg = new Image();
      tshirtImg.crossOrigin = 'anonymous';

      const imagePath = templateUrl ?? '';
      console.log('Loading t-shirt image from:', imagePath);

      tshirtImg.onload = () => {
        console.log('T-shirt image loaded successfully');
        // Draw t-shirt background
        ctx.drawImage(tshirtImg, 0, 0, tempCanvas.width, tempCanvas.height);

        // Get the current design directly from fabricImage
        if (fabricImage.current) {
          const design = fabricImage.current;

          // Get the source image element from the FabricImage
          const sourceImg = design.getElement() as HTMLImageElement;
          if (sourceImg) {
            const scaleX = design.scaleX || 1;
            const scaleY = design.scaleY || 1;
            const left = design.left || 0;
            const top = design.top || 0;
            const width = design.width! * scaleX;
            const height = design.height! * scaleY;
            const angle = design.angle || 0;

            // Calculate position (considering center origin)
            const x = left - width / 2;
            const y = top - height / 2;

            // Save context for transformation
            ctx.save();

            // Apply rotation if needed
            if (angle !== 0) {
              ctx.translate(left, top);
              ctx.rotate((angle * Math.PI) / 180);
              ctx.translate(-left, -top);
            }

            // Draw the design image
            ctx.drawImage(sourceImg, x, y, width, height);

            // Restore context
            ctx.restore();
          }
        }

        // Return the final mockup as data URL
        const dataUrl = tempCanvas.toDataURL('image/png');
        console.log(
          'Mockup generation complete, dataUrl length:',
          dataUrl.length
        );
        resolve(dataUrl);
      };

      tshirtImg.onerror = (error) => {
        console.error('Failed to load t-shirt image:', error);
        resolve(null);
      };

      tshirtImg.src = imagePath;
    });
  }, [currentSide, templateUrl]);

  useEffect(() => {
    setCurrentSide(side || Side.FRONT);
  }, [side]);

  // Preview mockup function
  const previewMockup = async () => {
    if (!fabricImage.current) {
      alert('Vui lòng tải lên thiết kế trước!');
      return;
    }

    setLoading(true);
    try {
      const mockupData = await generateMockup();
      if (mockupData) {
        setMockupPreview(mockupData);
        setShowMockupModal(true);
      } else {
        alert('Không thể tạo bản xem trước mockup.');
      }
    } catch (error) {
      console.error('Error generating mockup:', error);
      alert('Không thể tạo bản xem trước mockup.');
    } finally {
      setLoading(false);
    }
  };

  // Download mockup function
  const downloadMockup = async () => {
    if (!fabricImage.current) {
      alert('Vui lòng tải lên thiết kế trước!');
      return;
    }

    setLoading(true);
    try {
      const mockupData = await generateMockup();
      if (mockupData) {
        // Create download link
        const link = document.createElement('a');
        link.download = `tshirt-mockup-${
          color || 'template'
        }-${currentPrintArea}.png`;
        link.href = mockupData;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Không thể tạo mockup để tải xuống.');
      }
    } catch (error) {
      console.error('Error generating mockup:', error);
      alert('Không thể tạo mockup để tải xuống.');
    } finally {
      setLoading(false);
    }
  };

  const submitDesign = async () => {
    try {
      setLoading(true);

      // Check if user has uploaded a design
      if (!fileList.length || !fileList[0]?.originFileObj) {
        message.error('Vui lòng tải lên thiết kế trước!');
        return;
      }

      // Create FormData with the original uploaded file as the design
      const formData = new FormData();
      formData.append('design', fileList[0].originFileObj, fileList[0].name);

      // Generate mockup PNG for upload
      console.log('Generating mockup for template color:', color);
      console.log('fabricCanvas.current:', !!fabricCanvas.current);
      console.log('fabricImage.current:', !!fabricImage.current);

      const mockupDataUrl = await generateMockup();
      console.log(
        'Generated mockupDataUrl:',
        !!mockupDataUrl,
        mockupDataUrl ? 'has data' : 'null/empty'
      );

      // Create design with original file
      const res = await orderApi.createDesign(formData);

      // Mark design as submitted
      setDesignSubmitted(true);

      // Show success message
      message.success('Thiết kế đã được tải lên thành công!');

      // Pass both the design response and the generated mockup with selected print area and side
      onDesignSubmitted(
        res,
        mockupDataUrl || undefined,
        currentPrintArea,
        currentSide
      );
    } catch (e) {
      console.error(e);
      message.error('Không thể lưu thiết kế. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFile: UploadProps['onChange'] = (file: any) => {
    // Check file size before processing
    if (file?.fileList.length > 0) {
      const uploadedFile = file.fileList[0];
      if (uploadedFile.size && uploadedFile.size > DesignMaxSize) {
        message.error('Kích thước tối đa 30MB');
        return; // Don't process the file if it's too large
      }
    }

    setFileList(
      file?.fileList.length
        ? [
            {
              ...file.fileList[0],
              status:
                file.fileList[0].status === 'error'
                  ? 'done'
                  : file.fileList[0].status,
            },
          ]
        : file?.fileList
    );

    // Reset submitted state when a new file is uploaded
    if (file?.fileList.length > 0) {
      setDesignSubmitted(false);
    }
  };

  return (
    <>
      {loading && (
        <SpinContainer>
          <Spin size="large" />
        </SpinContainer>
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 10,
          padding: 10,
          alignItems: 'center',
          justifyContent: 'flex-start',
          backgroundColor: 'white',
          position: 'relative',
          zIndex: 100,
          marginBottom: 10,
          minHeight: 50,
          boxSizing: 'border-box',
        }}
      >
        <Upload
          name="file"
          onChange={handleOpenFile}
          multiple={false}
          fileList={fileList}
          accept="image/*"
        >
          {fileList?.length < 1 && (
            <PrimaryButton
              className="font-weight--500"
              style={{
                whiteSpace: 'nowrap',
                flex: '0 0 auto',
                width: 250,
              }}
            >
              Tải lên thiết kế (max 30MB)
            </PrimaryButton>
          )}
        </Upload>

        {fileList.length > 0 && (
          <PrimaryButton
            className="font-weight--500"
            onClick={() => {
              setFileList([]);
              setDesignSubmitted(false);
            }}
            style={{
              whiteSpace: 'nowrap',
              flex: '0 0 auto',
            }}
          >
            Xóa thiết kế
          </PrimaryButton>
        )}

        {/* Print Area and Side Selection */}
        <div
          style={{
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>
              Vùng in:
            </label>
            <select
              value={currentPrintArea}
              onChange={(e) =>
                setCurrentPrintArea(e.target.value as PrintAreaType)
              }
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #d9d9d9',
                fontSize: '12px',
              }}
            >
              {Object.values(PrintAreaType)
                .filter((printArea) => printAreaOptions.includes(printArea))
                .map((printArea) => (
                  <option key={printArea} value={printArea}>
                    {PRINT_AREA_OPTIONS_LABELS[printArea]}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {fileList.length > 0 && (
          <>
            <PrimaryButton
              onClick={previewMockup}
              className="font-weight--500"
              style={{
                whiteSpace: 'nowrap',
                flex: '0 0 auto',
              }}
            >
              Xem trước
            </PrimaryButton>
            <PrimaryButton
              onClick={downloadMockup}
              className="font-weight--500"
              style={{
                whiteSpace: 'nowrap',
                flex: '0 0 auto',
              }}
            >
              Tải xuống
            </PrimaryButton>

            <PrimaryButton
              onClick={submitDesign}
              className="font-weight--500"
              disabled={
                !fileList.length || !fabricImage.current || designSubmitted
              }
              style={{
                whiteSpace: 'nowrap',
                flex: '0 0 auto',
                backgroundColor: designSubmitted ? '#52c41a' : undefined,
                borderColor: designSubmitted ? '#52c41a' : undefined,
              }}
            >
              {designSubmitted ? '✓ Đã tải lên' : 'Tải lên'}
            </PrimaryButton>
          </>
        )}
      </div>

      <div className="m__b--10">
        <div
          style={{
            width: '100%',
            height: 'calc(100vh - 200px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: '2px solid #007BFF',
            borderRadius: '8px',
            boxSizing: 'border-box',
            overflow: 'hidden',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <canvas ref={canvasRef} />
        </div>

        <div style={{ marginTop: 10, fontSize: 12, color: '#666' }}>
          <p>
            <strong>Mặt:</strong> {currentSide === Side.FRONT ? 'Trước' : 'Sau'}{' '}
            |<strong> Vùng in:</strong>{' '}
            {currentPrintArea.replace('_', ' ').toUpperCase()}
          </p>
          {debugMode && (
            <div
              style={{
                background: '#fff3cd',
                border: '1px solid #ffeaa7',
                padding: 10,
                margin: '10px 0',
                borderRadius: 4,
              }}
            >
              <p>
                <strong>🔧 Chế độ Debug đang hoạt động:</strong>
              </p>
              <p>
                Nhấp vào bất kỳ đâu trên áo để lấy tọa độ cho việc định vị vùng
                in.
              </p>
              {clickCoordinates && (
                <p style={{ color: '#d63031', fontWeight: 'bold' }}>
                  Lần nhấp cuối: x={clickCoordinates.x}, y={clickCoordinates.y}
                </p>
              )}
              <p style={{ fontSize: 11, fontStyle: 'italic' }}>
                Hiện tại: {currentPrintArea.replace('_', ' ')} - x=
                {TSHIRT_PRINT_AREAS[currentPrintArea].x}, y=
                {TSHIRT_PRINT_AREAS[currentPrintArea].y}, size=
                {TSHIRT_PRINT_AREAS[currentPrintArea].width}x
                {TSHIRT_PRINT_AREAS[currentPrintArea].height}
              </p>
            </div>
          )}
          <p>
            <strong>Hướng dẫn:</strong> Tải lên hình ảnh và định vị nó trong
            vùng in được đánh dấu. Thiết kế sẽ được giới hạn trong vùng in đã
            chọn.
          </p>
        </div>
      </div>

      {/* Mockup Preview Modal */}
      <Modal
        title="Xem trước Mockup áo thun"
        open={showMockupModal}
        onCancel={() => setShowMockupModal(false)}
        footer={[
          <PrimaryButton key="download" onClick={downloadMockup}>
            Tải xuống
          </PrimaryButton>,
          <PrimaryButton
            key="close"
            onClick={() => setShowMockupModal(false)}
            style={{ marginLeft: 8 }}
          >
            Đóng
          </PrimaryButton>,
        ]}
        width={700}
        centered
      >
        {mockupPreview && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <img
              src={mockupPreview}
              alt="Xem trước Mockup áo thun"
              style={{
                maxWidth: '100%',
                maxHeight: '500px',
                objectFit: 'contain',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
              }}
            />
            <p style={{ marginTop: 16, color: '#666' }}>
              <strong>Màu:</strong> {color || 'Mặc định'} |
              <strong> Vùng in:</strong>{' '}
              {TSHIRT_PRINT_AREAS[currentPrintArea]?.description}
            </p>
          </div>
        )}
      </Modal>
    </>
  );
};

export default TShirtMockupEditor;
